import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from './auth';

/**
 * Groups in the Graphs tab ("Max Müller", "Management Meeting"): named,
 * ordered lists of the user's saved graphs of one assistant. A graph can be
 * in many groups; a group holds links, never copies (backend
 * /api/graph-groups, src/model/graph_group.py).
 *
 * Every change shows at once (optimistic) and is settled with the group the
 * server answers. Changes to one group go out one after the other, and only
 * the answer to the LATEST change is applied — so dragging quickly twice
 * never makes the list jump back. A failed change reloads the groups.
 *
 * Lives in EfdAgentChat (not in the tab), so the open group survives a
 * switch to the chat and back.
 */

/** Thrown when a group of that name exists already (HTTP 409). */
export class GroupNameTaken extends Error {}

const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id - b.id;

/** `ids` with the id at `from` moved to index `to` (drag & drop). */
export function moveItem(ids, from, to) {
  const next = ids.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function useGraphGroups(agentKey) {
  const [groups, setGroups] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const queues = useRef({});        // group id -> promise of its last request
  const latest = useRef({});        // group id -> number of its latest change

  const request = useCallback(async (method, path, body) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: getAuthHeaders(body ? { 'Content-Type': 'application/json' } : {}),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 409) throw new GroupNameTaken();
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await request('GET', `/api/graph-groups?agent=${agentKey}`);
      setGroups(Array.isArray(data) ? data.slice().sort(byName) : []);
    } catch (e) {
      console.error('Failed to load graph groups:', e);
    }
  }, [agentKey, request]);

  useEffect(() => { load(); }, [load]);

  const store = (group) => setGroups(gs => [...gs.filter(g => g.id !== group.id), group].sort(byName));

  /** Optimistic change of one group's graphs, then the server's answer. */
  const change = (id, optimistic, method, path, ids) => {
    if (!groupsRef.current.some(g => g.id === id)) return Promise.resolve();
    const mine = (latest.current[id] || 0) + 1;
    latest.current[id] = mine;
    setGroups(gs => gs.map(g => (g.id === id ? { ...g, graph_ids: optimistic(g.graph_ids) } : g)));
    const run = (queues.current[id] || Promise.resolve())
      .catch(() => {})
      .then(() => request(method, path, { ids }))
      .then((group) => {
        if (latest.current[id] === mine) store(group);
      })
      .catch((e) => {
        load();
        throw e;
      });
    queues.current[id] = run;
    return run;
  };

  const create = async (name, ids = []) => {
    const group = await request('POST', '/api/graph-groups', { agent: agentKey, name, ids });
    store(group);
    return group;
  };

  const rename = async (id, name) => {
    const group = await request('PATCH', `/api/graph-groups/${id}`, { name });
    store(group);
    return group;
  };

  const remove = async (id) => {
    const before = groupsRef.current;
    setGroups(gs => gs.filter(g => g.id !== id));
    setActiveId(a => (a === id ? null : a));
    try {
      await request('DELETE', `/api/graph-groups/${id}`);
    } catch (e) {
      setGroups(before);
      throw e;
    }
  };

  const add = (id, ids) => change(id, cur => [...cur, ...ids.filter(i => !cur.includes(i))],
    'POST', `/api/graph-groups/${id}/graphs`, ids);

  const removeGraphs = (id, ids) => change(id, cur => cur.filter(i => !ids.includes(i)),
    'POST', `/api/graph-groups/${id}/graphs/remove`, ids);

  const reorder = (id, ids) => change(id, () => ids, 'PUT', `/api/graph-groups/${id}/order`, ids);

  /** Graphs deleted (Graphs tab or the star in the chat) leave their groups —
   *  the server already dropped the links. */
  const forgetGraphs = useCallback((ids) => {
    setGroups(gs => gs.map(g => ({ ...g, graph_ids: g.graph_ids.filter(i => !ids.includes(i)) })));
  }, []);

  return { groups, activeId, setActiveId, create, rename, remove, add, removeGraphs, reorder, forgetGraphs };
}
